import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Check, Receipt, Tag, RefreshCw, Globe } from 'lucide-react';
import { SiteHeader } from '../../components/SiteHeader';
import { SiteFooter } from '../../components/SiteFooter';
import { CurrencySelect } from '../../components/ui/CurrencySelect';
import { Reveal } from '../../components/landing/Reveal';
import { fadeUp, staggerContainer, useRevealProps } from '../../components/landing/motion';
import { currency as currencyMeta } from '../../lib/catalogs';
import { api, API_ORIGIN } from '../../lib/api';
import { useDocumentMeta } from '../../lib/useDocumentMeta';

type Plan = 'free' | 'growth' | 'global' | 'enterprise';

interface PlanPricing {
  perSeatMonthly: number;
  perSeatYearly: number;
  seatMin: number;
  seatMax: number | null;
}

const FEATURE_PREVIEW_COUNT = 6;

// Conservative mid-2026 USD-base rates — used only as a fallback when
// /fx/latest hasn't returned yet. The cron populates fresh ECB rates
// nightly so production values come from `/fx/latest` not this table.
const FX_FALLBACK: Record<string, number> = {
  USD: 1, JPY: 155, EUR: 0.93, GBP: 0.78, CHF: 0.88,
  SGD: 1.34, KRW: 1340, HKD: 7.82, CNY: 7.26, TWD: 32.5,
  MYR: 4.7, THB: 36, IDR: 16_300, PHP: 58, VND: 25_400, INR: 84,
  CAD: 1.37, MXN: 18.5,
  AUD: 1.51, NZD: 1.66,
  SEK: 10.5, NOK: 10.8, DKK: 6.92, PLN: 4.0,
  BRL: 5.1, AED: 3.67, ZAR: 18.4,
};

// i18n keys (pricing.feat.*) — translated per locale.
const FEATURE_KEYS: Record<Plan, string[]> = {
  free: ['free5Seats', 'oneEntity', 'invoices50', 'aiLight', 'pintSend'],
  growth: ['unlimitedInvoices', 'oneEntity', 'jpComplianceAll', 'docOcrMatch', 'arCollections', 'pintSendReceive', 'aiCap25', 'slackTeamsLineworks'],
  global: ['unlimitedEntities', 'multiCurrencyFx', 'crossBorderRails', 'vendorPortal', 'reporting1099', 'aiCap50', 'peppolPriority'],
  enterprise: ['sso', 'auditLog', 'jpRegistryFeed', 'soc2', 'sla', 'customAiRetention', 'regionPinning'],
};

type AiBand = 'light' | 'standard' | 'heavy';
const AI_BAND_COST_PER_USER_USD: Record<AiBand, number> = { light: 4, standard: 12, heavy: 40 };

export function PricingPage() {
  const { t } = useTranslation();
  useDocumentMeta({
    title: 'Pricing — PlyEZ',
    description: 'Public per-seat pricing for PlyEZ. Free, Growth ($49), Global ($99), Enterprise ($189). Includes a live multi-currency calculator.',
    path: '/pricing',
  });
  const [pricing, setPricing] = useState<Record<Plan, PlanPricing> | null>(null);
  const [seats, setSeats] = useState(15);
  const [plan, setPlan] = useState<Plan>('global');
  const [currency, setCurrency] = useState<string>('USD');
  const [aiBand, setAiBand] = useState<AiBand>('standard');
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');

  useEffect(() => {
    // Live Stripe-sourced prices (monthly + yearly per seat). Fall back to
    // the static table only if the request fails. The yearly figure is the
    // ANNUAL per-seat amount; the calculator shows its monthly equivalent.
    api.get<Record<Plan, PlanPricing>>('/billing/pricing')
      .then((r) => setPricing(r.data))
      .catch(() => {
        setPricing({
          free: { perSeatMonthly: 0, perSeatYearly: 0, seatMin: 1, seatMax: 5 },
          growth: { perSeatMonthly: 49, perSeatYearly: 490, seatMin: 1, seatMax: null },
          global: { perSeatMonthly: 99, perSeatYearly: 990, seatMin: 1, seatMax: null },
          enterprise: { perSeatMonthly: 189, perSeatYearly: 1890, seatMin: 5, seatMax: null },
        });
      });
  }, []);

  useEffect(() => {
    api.get<{ suggestedCurrency: string }>('/geo/hint').then((r) => {
      if (r.data?.suggestedCurrency) setCurrency(r.data.suggestedCurrency);
    }).catch(() => {});
  }, []);

  // Hooks must run on every render — call before the early return below, or the
  // hook count changes once `pricing` loads (React error #310). (Caught by the
  // production browser smoke; static gates don't see hooks-order violations.)
  const cardsReveal = useRevealProps();

  if (!pricing) return null;

  const fxRate = FX_FALLBACK[currency] ?? 1;
  const symbol = currencyMeta(currency)?.symbol ?? currency;
  const cfg = pricing[plan];
  // Per-seat USD for the chosen cycle: yearly prices are annual amounts, so
  // divide by 12 to compare like-for-like in the monthly calculator.
  const perSeatUsd = (p: PlanPricing) =>
    interval === 'yearly' ? (p.perSeatYearly || p.perSeatMonthly * 12) / 12 : p.perSeatMonthly;
  const seatCount = Math.max(cfg.seatMin, Math.min(seats, cfg.seatMax ?? seats));
  const monthlySeatUsd = perSeatUsd(cfg) * seatCount;
  const monthlyAiUsd = AI_BAND_COST_PER_USER_USD[aiBand] * seatCount;
  const monthlyUsd = monthlySeatUsd + monthlyAiUsd;
  const monthlyLocal = Math.round(monthlyUsd * fxRate);
  const annualLocal = monthlyLocal * 12;
  const aiLocal = Math.round(monthlyAiUsd * fxRate);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <section data-landing className="washi">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
          <Reveal as="div" className="text-center max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 rounded-full pill-pearl px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em]">
              <span className="sparkle-dot" aria-hidden />
              {t('pricing.eyebrow')}
            </span>
            <h1 className="display text-4xl lg:text-5xl font-semibold mt-4">{t('pricing.title')}</h1>
            <p className="mt-3 text-muted-foreground">{t('pricing.subtitle')}</p>
          </Reveal>

          <Reveal as="div" delay={0.04} className="mt-10 max-w-4xl mx-auto">
            <TransparentPricingCallout />
          </Reveal>

          <Reveal
            as="div"
            delay={0.05}
            className="mt-8 max-w-4xl mx-auto rounded-2xl border border-border bg-card shadow-soft overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
              <span className="font-medium">{t('pricing.calcTitle')}</span>
              <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/50 text-xs font-semibold">
                {(['monthly', 'yearly'] as const).map((iv) => (
                  <button
                    key={iv}
                    type="button"
                    onClick={() => setInterval(iv)}
                    className={'px-3 py-1.5 rounded transition-colors ' + (interval === iv ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}
                  >
                    {iv === 'monthly' ? t('pricing.monthly', { defaultValue: 'Monthly' }) : t('pricing.yearlyDiscount', { defaultValue: 'Yearly · −17%' })}
                  </button>
                ))}
              </div>
            </div>
            <div className="px-6 py-6">
              <div className="grid md:grid-cols-3 gap-6 items-end">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t('pricing.seats')}</label>
                  <input
                    type="number"
                    min={1}
                    value={seats}
                    onChange={(e) => setSeats(Math.max(1, Number(e.target.value)))}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-border bg-background num focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t('pricing.plan')}</label>
                  <select
                    value={plan}
                    onChange={(e) => setPlan(e.target.value as Plan)}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-border bg-background focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    <option value="free">{t('pricing.freeTier')}</option>
                    <option value="growth">{t('pricing.growthTier')}</option>
                    <option value="global">{t('pricing.globalTier')}</option>
                    <option value="enterprise">{t('pricing.enterpriseTier')}</option>
                  </select>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-1">{t('common.currency')}</div>
                  <CurrencySelect value={currency} onChange={setCurrency} />
                </div>
              </div>
              <div className="mt-6">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t('pricing.aiBand')}</label>
                  <span className="num text-xs text-muted-foreground">+ {symbol}{aiLocal.toLocaleString()} / {t('pricing.month')}</span>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {(['light', 'standard', 'heavy'] as const).map((b) => (
                    <button
                      key={b}
                      type="button"
                      onClick={() => setAiBand(b)}
                      className={`text-left rounded-lg border px-3 py-2 transition-colors ${
                        aiBand === b
                          ? 'border-accent-500 bg-cinnabar-500/[0.06] dark:bg-cinnabar-500/[0.12]'
                          : 'border-border hover:border-accent-300 dark:hover:border-accent-700'
                      }`}
                    >
                      <div className="text-sm font-medium">{t(`pricing.aiBands.${b}.title`)}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{t(`pricing.aiBands.${b}.body`)}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-8 grid md:grid-cols-2 gap-6">
                <div className="rounded-xl border border-border bg-muted/50 p-5">
                  <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t('pricing.monthlyTotal')}</div>
                  <div className="num text-4xl font-semibold mt-1">
                    {symbol}{monthlyLocal.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {seatCount} {t('pricing.seats')} · {plan} · {t(`pricing.aiBands.${aiBand}.title`)}
                  </div>
                </div>
                <div className="rounded-xl border border-border bg-muted/50 p-5">
                  <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t('pricing.annualTotal')}</div>
                  <div className="num text-4xl font-semibold mt-1">
                    {symbol}{annualLocal.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{t('pricing.billedMonthly')}</div>
                </div>
              </div>
            </div>
          </Reveal>

          <motion.div
            variants={staggerContainer}
            {...cardsReveal}
            className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {(['free', 'growth', 'global', 'enterprise'] as Plan[]).map((p) => (
              <PlanCard key={p} plan={p} pricing={pricing[p]} interval={interval} highlighted={p === 'global'} />
            ))}
          </motion.div>

          <Reveal
            as="div"
            className="mt-12 max-w-4xl mx-auto rounded-2xl border border-border bg-card shadow-soft overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-border font-medium">{t('pricing.aiCostTitle')}</div>
            <div className="px-6 py-5 text-sm text-muted-foreground leading-relaxed">
              <p>{t('pricing.aiCostBody')}</p>
              <ul className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                {(['light', 'standard', 'heavy'] as const).map((b) => (
                  <li key={b} className="rounded-xl border border-border p-3">
                    <div className="font-medium text-foreground">{t(`pricing.aiBands.${b}.title`)}</div>
                    <div className="num mt-0.5">≈ ${AI_BAND_COST_PER_USER_USD[b]} / {t('pricing.perUserMo')}</div>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}

// Transparent, fee-light positioning. The four points below are literally
// true of the product: published per-seat Stripe prices, no per-transaction
// fee on invoices paid, self-serve checkout (no "contact sales" gate), and a
// live multi-currency display. Restrained washi tile — sumi-ink + cinnabar.
export function TransparentPricingCallout() {
  const { t } = useTranslation();
  const points: Array<{ icon: typeof Receipt; key: string }> = [
    { icon: Receipt, key: 'noPerTxn' },
    { icon: Tag, key: 'noSalesGate' },
    { icon: RefreshCw, key: 'cancelAnytime' },
    { icon: Globe, key: 'allCurrencies' },
  ];
  return (
    <div className="washi rounded-2xl border border-border bg-card shadow-soft overflow-hidden">
      <div className="px-6 py-5 sm:px-8 sm:py-7">
        <div className="flex items-center gap-2">
          <span className="sparkle-dot" aria-hidden />
          <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-accent-700 dark:text-accent-400">
            {t('pricing.transparent.title')}
          </span>
        </div>
        <ul className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-3">
          {points.map(({ icon: Icon, key }) => (
            <li key={key} className="flex items-start gap-2.5 text-sm">
              <Icon size={16} className="mt-0.5 text-accent-600 shrink-0" aria-hidden />
              <span className="text-foreground/90">{t(`pricing.transparent.${key}`)}</span>
            </li>
          ))}
        </ul>
        <p className="mt-5 border-t border-border pt-4 text-sm text-muted-foreground italic">
          {t('pricing.transparent.compareLine')}
        </p>
      </div>
    </div>
  );
}

function PlanCard({ plan, pricing, interval, highlighted }: { plan: Plan; pricing: PlanPricing; interval: 'monthly' | 'yearly'; highlighted?: boolean }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const labels: Record<Plan, string> = {
    free: t('pricing.freeTier'),
    growth: t('pricing.growthTier'),
    global: t('pricing.globalTier'),
    enterprise: t('pricing.enterpriseTier'),
  };
  const ctaLabel: Record<Plan, string> = {
    free: t('pricing.startFree'),
    growth: t('pricing.tryGrowth'),
    global: t('pricing.tryGlobal'),
    enterprise: t('pricing.tryEnterprise', { defaultValue: 'Try Enterprise' }),
  };
  // Monthly-equivalent per-seat price for the chosen cycle.
  const perSeat = interval === 'yearly'
    ? Math.round((pricing.perSeatYearly || pricing.perSeatMonthly * 12) / 12)
    : pricing.perSeatMonthly;
  const features = FEATURE_KEYS[plan];
  const visible = expanded ? features : features.slice(0, FEATURE_PREVIEW_COUNT);
  const hidden = features.length - FEATURE_PREVIEW_COUNT;
  return (
    <motion.div
      variants={fadeUp}
      className={
        'flex flex-col rounded-2xl border bg-card p-6 shadow-soft transition-colors ' +
        (highlighted ? 'border-accent-500 ring-1 ring-accent-500/40' : 'border-border')
      }
    >
      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{labels[plan]}</div>
      {perSeat > 0 ? (
        <div className="mt-2">
          <span className="num text-3xl font-semibold">${perSeat}</span>
          <span className="text-sm text-muted-foreground"> / {t('pricing.perUserMo')}</span>
          {interval === 'yearly' && (
            <div className="text-[11px] text-muted-foreground mt-0.5">{t('pricing.billedYearly', { defaultValue: 'billed yearly' })}</div>
          )}
        </div>
      ) : (
        <div className="mt-2 num text-3xl font-semibold">$0</div>
      )}
      <ul className="mt-4 space-y-2 text-sm flex-1">
        {visible.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check size={16} className="mt-0.5 text-accent-600 shrink-0" />
            <span>{t(`pricing.feat.${f}`)}</span>
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 self-start text-xs font-semibold text-accent-600 hover:underline"
        >
          {expanded
            ? t('pricing.showLess', { defaultValue: 'Show less' })
            : t('pricing.showAll', { count: hidden, defaultValue: 'Show all features' })}
        </button>
      )}
      {/* All paid tiers (including Enterprise) are real self-serve
          subscriptions wired to Stripe — the previous "Talk to sales"
          CTA for Enterprise didn't match the product, since Enterprise
          has its own published $189/seat price and Stripe price IDs.
          Free signs up the same way; Stripe Checkout is only invoked
          from in-app after the user picks a paid tier. */}
      <a
        href={`${API_ORIGIN}/auth/google`}
        className={`mt-6 block w-full text-center py-2.5 rounded-lg text-sm font-medium ${
          highlighted
            ? 'bg-foreground text-background hover:bg-foreground/90'
            : 'border border-border hover:bg-muted'
        }`}
      >
        {ctaLabel[plan]}
      </a>
    </motion.div>
  );
}
